import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { timeAgo } from "@/lib/lifeos";
import { speak, stopSpeaking, briefingToText, speechSupported } from "@/lib/speak";
import {
  X, Crown, Loader2, Play, Power, Check, Trash2, KeyRound, ShieldCheck, ExternalLink,
  ChevronDown, ChevronRight, Sparkles, Plug, Copy, ClipboardList, Brain, Upload, Lightbulb, HelpCircle,
  Sunrise, RefreshCw, MessageCircle, Send, Volume2, Square,
} from "lucide-react";

const INTERVALS = [10, 15, 20, 30, 60, 120];

function Section({ title, icon: Icon, children, right }) {
  return (
    <section className="rounded-xl border border-white/10 bg-white/[0.02] p-5 mb-4">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon size={16} className="text-cyan-300" />}
        <h3 className="font-tactical uppercase tracking-wider text-sm text-zinc-200">{title}</h3>
        {right && <div className="ml-auto">{right}</div>}
      </div>
      {children}
    </section>
  );
}

function DirectiveCard({ d, onConfirm, onDismiss }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const done = d.status !== "needs_confirm";
  const isConnect = d.kind === "connect_request";
  const accent = isConnect ? "#ffb454" : "#00f0ff";

  const act = async (kind) => {
    setBusy(kind);
    try { kind === "confirm" ? await onConfirm(d.id) : await onDismiss(d.id); }
    finally { setBusy(""); }
  };

  return (
    <article data-testid="boss-directive-card" className={`rounded-xl border bg-[#09111c] p-4 transition-opacity ${done ? "opacity-50 border-white/5" : "border-white/10"}`}>
      <div className="flex items-start gap-3">
        <span className="shrink-0 grid place-items-center rounded-lg mt-0.5" style={{ width: 30, height: 30, background: `${accent}14`, border: `1px solid ${accent}44`, color: accent }}>
          {isConnect ? <Plug size={15} /> : <ClipboardList size={15} />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded-full" style={{ color: accent, background: `${accent}14` }}>
              {isConnect ? "Connect needed" : "Work done"}
            </span>
            <span className="text-[10px] font-mono text-zinc-500">LifeOS → {d.agent_name}</span>
            {d.brain === "own" && <span className="text-[9px] font-mono text-emerald-300">· your ChatGPT</span>}
          </div>
          <h4 className="text-sm font-semibold text-zinc-100 mt-1.5">{d.title}</h4>
          {d.instruction && <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{d.instruction}</p>}
        </div>
      </div>

      {isConnect ? (
        <div className="mt-3 rounded-lg border border-amber-400/20 bg-amber-500/[0.05] p-3">
          <p className="text-[11px] text-amber-200/90 mb-2">To let LifeOS actually do this, connect <strong>{d.connect_name}</strong>. It's quick — follow these steps:</p>
          <ol className="space-y-1.5 text-[11px] text-zinc-300 list-decimal pl-4 marker:text-amber-300">
            {(d.connect_steps || []).map((s, i) => <li key={i}>{s}</li>)}
          </ol>
          {d.connect_where && <p className="text-[10px] text-zinc-500 mt-2">Where: {d.connect_where}</p>}
        </div>
      ) : (
        d.deliverable_preview && (
          <div className="mt-3">
            <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 text-[10px] font-mono text-zinc-400 hover:text-cyan-300 mb-1">
              {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />} {open ? "Hide" : "Preview"} the work
            </button>
            {open && (
              <pre className="text-[12px] leading-relaxed text-zinc-300 whitespace-pre-wrap font-sans bg-[#05090f] border border-white/10 rounded-lg p-3 max-h-64 overflow-y-auto thin-scroll">{d.deliverable_preview}{d.deliverable_preview.length >= 590 ? "…\n\n(full version in Deliverables)" : ""}</pre>
            )}
          </div>
        )
      )}

      {done ? (
        <div data-testid="boss-directive-result" className={`mt-3 text-[11px] font-mono ${d.status === "confirmed" ? "text-emerald-300" : "text-zinc-500"}`}>
          {d.status === "confirmed" ? "✓ " : "✕ "}{d.result || d.status}
        </div>
      ) : (
        <div className="mt-4 flex items-center gap-2">
          <button data-testid="boss-directive-confirm" disabled={!!busy} onClick={() => act("confirm")}
            className="inline-flex items-center gap-1.5 bg-cyan-500 hover:bg-cyan-400 text-[#04070c] text-xs font-bold uppercase tracking-wider rounded-lg px-3 h-9 transition-all disabled:opacity-60">
            {busy === "confirm" ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {isConnect ? "I've connected it" : "Approve"}
          </button>
          <button data-testid="boss-directive-dismiss" disabled={!!busy} onClick={() => act("dismiss")}
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-red-300 border border-white/10 hover:border-red-400/30 rounded-lg px-3 h-9 transition-colors">
            {busy === "dismiss" ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Dismiss
          </button>
        </div>
      )}
    </article>
  );
}

function KeyCard({ state, onChanged }) {
  const [showGuide, setShowGuide] = useState(false);
  const [key, setKey] = useState("");
  const [model, setModel] = useState(state.key_model || "gpt-4o");
  const [busy, setBusy] = useState("");
  const connected = state.key_status !== "not_connected";

  const act = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "save") { await api.saveBossKey(key.trim(), model); setKey(""); toast.success("ChatGPT key saved & encrypted. Verify it to confirm."); }
      if (kind === "verify") { const r = await api.verifyBossKey(); r.ok ? toast.success(r.message) : toast.error(r.message); }
      if (kind === "remove") { if (!window.confirm("Remove your ChatGPT key? LifeOS will fall back to the built-in brain.")) return; await api.removeBossKey(); }
      await onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update your key"); }
    finally { setBusy(""); }
  };

  return (
    <Section title="Connect your ChatGPT" icon={KeyRound} right={
      <span className={`text-[10px] font-mono ${connected ? (state.key_status === "verified" ? "text-emerald-300" : "text-amber-300") : "text-zinc-500"}`}>
        {connected ? (state.key_status === "verified" ? "verified · running on your account" : "saved · verify to confirm") : "not connected · using built-in brain"}
      </span>
    }>
      <p className="text-xs text-zinc-400 mb-3">LifeOS runs on <strong>your own ChatGPT (OpenAI) key</strong> so the work is truly yours. No key yet? It still works on our built-in brain as backup.</p>
      <button data-testid="boss-key-guide-toggle" onClick={() => setShowGuide((s) => !s)} className="flex items-center gap-1 text-[11px] text-cyan-300 hover:text-cyan-200 mb-3">
        {showGuide ? <ChevronDown size={13} /> : <ChevronRight size={13} />} How do I get my key? (2 minutes)
      </button>
      {showGuide && (
        <ol className="mb-4 space-y-1.5 text-[11px] text-zinc-300 list-decimal pl-4 marker:text-cyan-400 rounded-lg border border-white/10 bg-[#05090f] p-3">
          <li>Go to <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" className="text-cyan-300 underline inline-flex items-center gap-0.5">platform.openai.com/api-keys <ExternalLink size={10} /></a> and sign in (this is different from chatgpt.com).</li>
          <li>Click <strong>Create new secret key</strong>, name it "LifeOS", and copy the key that starts with <code className="text-cyan-300">sk-</code>.</li>
          <li>Make sure billing is enabled on your OpenAI account (Settings → Billing) or the key can't run.</li>
          <li>Paste it below and click Save, then Verify.</li>
        </ol>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <input data-testid="boss-key-input" type="password" autoComplete="off" spellCheck={false} value={key} onChange={(e) => setKey(e.target.value)} placeholder={connected ? "Paste a new sk-... key to replace" : "Paste your sk-... key"}
          className="flex-1 bg-[#05090f] border border-white/10 rounded-lg h-10 px-3 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40" />
        <select data-testid="boss-key-model" value={model} onChange={(e) => setModel(e.target.value)} className="bg-[#05090f] border border-white/10 rounded-lg h-10 px-2 text-xs text-zinc-200">
          <option value="gpt-4o" className="bg-[#0b0e14]">gpt-4o</option>
          <option value="gpt-4.1" className="bg-[#0b0e14]">gpt-4.1</option>
          <option value="gpt-4o-mini" className="bg-[#0b0e14]">gpt-4o-mini</option>
        </select>
        <button data-testid="boss-key-save" disabled={busy === "save" || key.trim().length < 20} onClick={() => act("save")}
          className="inline-flex items-center gap-1.5 bg-cyan-500 hover:bg-cyan-400 text-[#04070c] text-xs font-bold rounded-lg px-4 h-10 disabled:opacity-50">
          {busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Save
        </button>
      </div>
      {connected && (
        <div className="flex items-center gap-2 mt-3">
          <button data-testid="boss-key-verify" disabled={!!busy} onClick={() => act("verify")} className="inline-flex items-center gap-1.5 text-xs text-zinc-300 border border-white/10 hover:border-cyan-400/30 rounded-lg px-3 h-8">
            {busy === "verify" ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />} Verify key
          </button>
          <button data-testid="boss-key-remove" disabled={!!busy} onClick={() => act("remove")} className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-red-300 px-2 h-8">
            <Trash2 size={13} /> Remove
          </button>
        </div>
      )}
    </Section>
  );
}

function ProfileCard({ profile, onSaved }) {
  const [form, setForm] = useState(profile);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(profile); }, [profile]);
  const field = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { await api.saveBossProfile(form); toast.success("Boss Briefing saved. LifeOS now knows you."); await onSaved(); }
    catch { toast.error("Could not save your briefing"); }
    finally { setBusy(false); }
  };
  const input = "w-full bg-[#05090f] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40";
  const rows = [
    ["name", "Your name", "e.g. Alex Rivera", false],
    ["occupation", "What you do", "e.g. freelance brand designer", false],
    ["goals", "Top goals right now", "e.g. Land 2 retainer clients this month; keep current clients delighted", true],
    ["priorities", "Priorities / what matters most", "e.g. Revenue first, then delivery quality", true],
    ["do_not_do", "Do NOT do (hard rules)", "e.g. Never quote pricing without my sign-off", true],
  ];
  return (
    <Section title="Boss Briefing — teach LifeOS about you" icon={Sparkles}>
      <p className="text-xs text-zinc-400 mb-4">Fill this once. LifeOS uses it in every decision so the work is actually about your life and business.</p>
      <div className="grid gap-3">
        {rows.map(([k, label, ph, area]) => (
          <div key={k}>
            <label className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">{label}</label>
            {area
              ? <textarea data-testid={`boss-profile-${k}`} rows={2} className={input + " mt-1"} value={form[k] || ""} placeholder={ph} onChange={(e) => field(k, e.target.value)} />
              : <input data-testid={`boss-profile-${k}`} className={input + " mt-1"} value={form[k] || ""} placeholder={ph} onChange={(e) => field(k, e.target.value)} />}
          </div>
        ))}
      </div>
      <button data-testid="boss-profile-save" disabled={busy} onClick={save} className="mt-4 inline-flex items-center gap-1.5 bg-cyan-500 hover:bg-cyan-400 text-[#04070c] text-xs font-bold uppercase tracking-wider rounded-lg px-4 h-9 disabled:opacity-60">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save briefing
      </button>
    </Section>
  );
}

function KnowledgeCard({ kb, onRefresh, onOpenInterview }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const r = await api.uploadChatExport(file, setProgress); toast.success(r.message); await onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not read that file"); }
    finally { setBusy(false); setProgress(null); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <Section title="What LifeOS knows about you" icon={Brain} right={
      <span className="text-[10px] font-mono text-zinc-500">{kb.sources?.length ? kb.sources.join(" · ") : "learning..."}</span>
    }>
      {kb.narrative
        ? <p data-testid="boss-knowledge-narrative" className="text-xs text-zinc-300 leading-relaxed bg-[#05090f] border border-white/10 rounded-lg p-3">{kb.narrative}</p>
        : <p className="text-xs text-zinc-500">LifeOS hasn't learned you yet. Do the quick interview or import your ChatGPT history below — then it works far smarter.</p>}
      {kb.facts?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {kb.facts.slice(-8).map((f, i) => <span key={i} className="text-[10px] text-cyan-200/80 bg-cyan-500/[0.07] border border-cyan-400/15 rounded-full px-2 py-0.5">{f}</span>)}
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-4">
        <button data-testid="boss-interview-open" onClick={onOpenInterview} className="inline-flex items-center gap-1.5 bg-cyan-500 hover:bg-cyan-400 text-[#04070c] text-xs font-bold rounded-lg px-3 h-9">
          <Sparkles size={14} /> {kb.interview_complete ? "Redo interview" : "Get-to-know-you interview"}
        </button>
        <button data-testid="boss-import-chatgpt" disabled={busy} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 border border-white/10 hover:border-cyan-400/30 text-zinc-200 text-xs rounded-lg px-3 h-9 disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Import my ChatGPT history
        </button>
        <button data-testid="boss-import-imessage" onClick={() => window.dispatchEvent(new CustomEvent("open-imessage"))} className="inline-flex items-center gap-1.5 border border-white/10 hover:border-cyan-400/30 text-zinc-200 text-xs rounded-lg px-3 h-9">
          <MessageCircle size={14} /> Connect iMessage
        </button>
        <input ref={fileRef} type="file" accept=".zip,.json,application/zip,application/json" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="boss-chatgpt-file" />
      </div>
      {progress && <p className="text-[10px] font-mono text-cyan-300 mt-2">{progress.label} · {progress.percent}%</p>}
      <p className="text-[10px] text-zinc-500 mt-3 leading-relaxed">
        Your OpenAI key can't read chatgpt.com chats (OpenAI limitation), so import them: in ChatGPT go to <strong>Settings → Data Controls → Export</strong>, they email you a .zip — upload it here. Everything stays private to your workspace.
      </p>
    </Section>
  );
}

function InterviewModal({ onClose, onDone }) {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.getBossInterview().then((d) => { setQuestions(d.questions); setAnswers(d.answers || {}); }); }, []);
  const submit = async () => {
    setBusy(true);
    try { await api.submitBossInterview(answers); toast.success("LifeOS just got to know you a lot better."); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/70 backdrop-blur-sm p-4" data-testid="boss-interview-modal">
      <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto thin-scroll rounded-2xl border border-cyan-400/25 bg-[#0a1220] p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display uppercase tracking-widest text-cyan-300 text-base flex items-center gap-2"><Sparkles size={16} /> Get to know me</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={16} /></button>
        </div>
        <p className="text-xs text-zinc-500 mb-4">Answer what you can. LifeOS turns this into how it works for you. You can redo it anytime.</p>
        <div className="space-y-4">
          {questions.map((q) => (
            <div key={q.id}>
              <label className="text-xs text-zinc-300 font-medium">{q.label}</label>
              <textarea data-testid={`boss-interview-${q.id}`} rows={2} value={answers[q.id] || ""} placeholder={q.placeholder}
                onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                className="mt-1 w-full bg-[#05090f] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40" />
            </div>
          ))}
        </div>
        <button data-testid="boss-interview-submit" disabled={busy} onClick={submit} className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-[#04070c] text-sm font-bold uppercase tracking-wider rounded-lg h-11 disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Teach LifeOS about me
        </button>
      </div>
    </div>
  );
}

function RecommendationCard({ r, onAccept, onDismiss }) {
  const [busy, setBusy] = useState("");
  const done = r.status !== "suggested";
  const act = async (k) => { setBusy(k); try { k === "accept" ? await onAccept(r.id) : await onDismiss(r.id); } finally { setBusy(""); } };
  return (
    <article data-testid="boss-recommendation-card" className={`rounded-xl border bg-[#09111c] p-3.5 ${done ? "opacity-50 border-white/5" : "border-white/10"}`}>
      <div className="flex items-start gap-2.5">
        <Lightbulb size={16} className="text-amber-300 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-semibold text-zinc-100">{r.title}</h4>
          {r.rationale && <p className="text-xs text-zinc-400 mt-0.5 leading-relaxed">{r.rationale}</p>}
          <p className="text-[10px] font-mono text-zinc-500 mt-1">LifeOS suggests → {r.agent_name || "an agent"}</p>
        </div>
      </div>
      {done ? (
        <div className="mt-2 text-[11px] font-mono text-zinc-500">{r.status === "accepted" ? "✓ Sent to work — see the desk" : "✕ Dismissed"}</div>
      ) : (
        <div className="mt-3 flex items-center gap-2">
          <button data-testid="boss-rec-accept" disabled={!!busy} onClick={() => act("accept")} className="inline-flex items-center gap-1.5 bg-amber-400 hover:bg-amber-300 text-[#04070c] text-xs font-bold rounded-lg px-3 h-8 disabled:opacity-60">
            {busy === "accept" ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Do it
          </button>
          <button data-testid="boss-rec-dismiss" disabled={!!busy} onClick={() => act("dismiss")} className="text-xs text-zinc-400 hover:text-red-300 px-2 h-8">Dismiss</button>
        </div>
      )}
    </article>
  );
}

function QuestionCard({ q, onAnswer, onDismiss }) {
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState("");
  const done = q.status !== "open";
  const act = async (k) => {
    setBusy(k);
    try { if (k === "answer") { if (!answer.trim()) return; await onAnswer(q.id, answer.trim()); } else await onDismiss(q.id); }
    finally { setBusy(""); }
  };
  return (
    <article data-testid="boss-question-card" className={`rounded-xl border bg-[#09111c] p-3.5 ${done ? "opacity-50 border-white/5" : "border-violet-400/20"}`}>
      <div className="flex items-start gap-2.5">
        <HelpCircle size={16} className="text-violet-300 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-zinc-100">{q.question}</p>
          {q.why && <p className="text-[10px] text-zinc-500 mt-0.5">{q.why}</p>}
          {done ? (
            <p className="text-[11px] font-mono text-emerald-300 mt-1.5">{q.status === "answered" ? `✓ ${q.answer}` : "✕ dismissed"}</p>
          ) : (
            <div className="mt-2 flex items-center gap-2">
              <input data-testid="boss-question-input" value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Your answer..." className="flex-1 bg-[#05090f] border border-white/10 rounded-lg h-9 px-3 text-xs text-zinc-100 focus:outline-none focus:ring-2 focus:ring-violet-400/40" />
              <button data-testid="boss-question-answer" disabled={!!busy || !answer.trim()} onClick={() => act("answer")} className="inline-flex items-center gap-1 bg-violet-500 hover:bg-violet-400 text-white text-xs font-semibold rounded-lg px-3 h-9 disabled:opacity-50">
                {busy === "answer" ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              </button>
              <button data-testid="boss-question-dismiss" disabled={!!busy} onClick={() => act("dismiss")} className="text-zinc-500 hover:text-red-300 px-1"><Trash2 size={13} /></button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function BriefingCard({ briefing, onRefresh, refreshing }) {
  const [speaking, setSpeaking] = useState(false);
  useEffect(() => () => stopSpeaking(), []);
  if (!briefing) return null;
  const toggleSpeak = () => {
    if (speaking) { stopSpeaking(); setSpeaking(false); return; }
    speak(briefingToText(briefing), { onStart: () => setSpeaking(true), onEnd: () => setSpeaking(false) });
  };
  return (
    <div data-testid="boss-briefing" className="rounded-xl border border-amber-400/25 bg-gradient-to-br from-[#0d1526] to-[#120e08] p-5 mb-5">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center rounded-lg shrink-0" style={{ width: 34, height: 34, background: "#ffd45a1a", border: "1px solid #ffd45a44" }}><Sunrise size={18} className="text-amber-300" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-zinc-100 font-medium">{briefing.greeting}</p>
          {briefing.decisions?.length > 0 && (
            <ol className="mt-3 space-y-2">
              {briefing.decisions.map((d, i) => (
                <li key={i} className="flex gap-2.5" data-testid="boss-briefing-decision">
                  <span className="grid place-items-center shrink-0 w-5 h-5 rounded-full bg-amber-400/15 text-amber-300 text-[10px] font-bold font-mono mt-0.5">{i + 1}</span>
                  <div><p className="text-xs text-zinc-100 font-medium">{d.title}</p><p className="text-[11px] text-zinc-500">{d.why}</p></div>
                </li>
              ))}
            </ol>
          )}
          {briefing.closing && <p className="text-[11px] text-amber-200/70 mt-3 italic">{briefing.closing}</p>}
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          {speechSupported() && (
            <button data-testid="boss-briefing-speak" onClick={toggleSpeak} title={speaking ? "Stop" : "Read aloud"} className="text-zinc-500 hover:text-amber-300 p-1">{speaking ? <Square size={14} /> : <Volume2 size={14} />}</button>
          )}
          <button data-testid="boss-briefing-refresh" onClick={onRefresh} disabled={refreshing} title="Refresh briefing" className="text-zinc-500 hover:text-amber-300 p-1">{refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}</button>
        </div>
      </div>
    </div>
  );
}

function BossChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { if (open) api.bossChatHistory().then((d) => setMessages(d.messages || [])).catch(() => {}); }, [open]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);
  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput(""); setMessages((m) => [...m, { role: "user", content: text }]); setBusy(true);
    try { const r = await api.bossChat(text); setMessages((m) => [...m, { role: "assistant", content: r.reply, order_started: r.order_started }]); }
    catch (e) { toast.error(e.response?.data?.detail || "LifeOS couldn't respond"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid="boss-chat-open" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 rounded-xl px-3 h-10 text-xs font-semibold border border-white/10 bg-white/5 text-zinc-200 hover:border-cyan-400/30">
        <MessageCircle size={14} /> Talk to LifeOS
      </button>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4" data-testid="boss-chat-modal" onClick={() => setOpen(false)}>
          <div className="w-full sm:max-w-lg h-[80vh] sm:h-[70vh] flex flex-col rounded-t-2xl sm:rounded-2xl border border-cyan-400/25 bg-[#0a1220]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 p-4 border-b border-white/10">
              <Crown size={16} className="text-cyan-300" />
              <h3 className="font-display uppercase tracking-widest text-cyan-300 text-sm">Chat with LifeOS</h3>
              <button onClick={() => setOpen(false)} className="ml-auto text-zinc-500 hover:text-white"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-y-auto thin-scroll p-4 space-y-3">
              {messages.length === 0 && <p className="text-xs text-zinc-500 text-center mt-8">Ask LifeOS what it's learned about you, or give it an order like "draft outreach to Priya".</p>}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div data-testid={`boss-chat-${m.role}`} className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-cyan-500 text-[#04070c]" : "bg-white/[0.06] text-zinc-200 border border-white/10"}`}>{m.content}{m.order_started && <span className="block mt-1 text-[10px] text-emerald-300 font-mono">▸ work started · see the desk</span>}</div>
                </div>
              ))}
              {busy && <div className="flex justify-start"><div className="rounded-2xl px-3.5 py-2 bg-white/[0.06] border border-white/10 text-zinc-400"><Loader2 size={14} className="animate-spin" /></div></div>}
              <div ref={endRef} />
            </div>
            <div className="p-3 border-t border-white/10 flex items-center gap-2">
              <input data-testid="boss-chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Message LifeOS or give an order..." className="flex-1 bg-[#05090f] border border-white/10 rounded-xl h-11 px-3 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40" />
              <button data-testid="boss-chat-send" onClick={send} disabled={busy || !input.trim()} className="w-11 h-11 grid place-items-center rounded-xl bg-cyan-500 hover:bg-cyan-400 text-[#04070c] disabled:opacity-50"><Send size={16} /></button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function AIBoss({ onClose, onGoConnections }) {
  const [state, setState] = useState(null);
  const [directives, setDirectives] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [kb, setKb] = useState({ facts: [], sources: [] });
  const [recs, setRecs] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [interviewOpen, setInterviewOpen] = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [refreshingBrief, setRefreshingBrief] = useState(false);
  const [running, setRunning] = useState(false);
  const pollRef = useRef(null);

  const loadDirectives = useCallback(async () => {
    const [d, c, r, q] = await Promise.all([api.bossDirectives(), api.bossCycles(), api.bossRecommendations(), api.bossQuestions()]);
    setDirectives(d); setCycles(c); setRecs(r); setQuestions(q);
  }, []);
  const loadKb = useCallback(async () => { setKb(await api.bossKnowledge()); }, []);
  const loadState = useCallback(async () => { const s = await api.bossState(); setState(s); setRunning(s.running); return s; }, []);

  useEffect(() => { loadState().catch(() => {}); loadDirectives().catch(() => {}); loadKb().catch(() => {}); api.bossBriefing().then(setBriefing).catch(() => {}); }, [loadState, loadDirectives, loadKb]);

  const refreshBriefing = async () => { setRefreshingBrief(true); try { setBriefing(await api.bossBriefing(true)); } catch { toast.error("Couldn't refresh briefing"); } finally { setRefreshingBrief(false); } };

  // Poll while the Boss is working so new work streams in
  useEffect(() => {
    if (!running) return;
    pollRef.current = setInterval(async () => {
      const s = await loadState();
      await loadDirectives();
      if (!s.running) { await loadKb(); clearInterval(pollRef.current); }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [running, loadState, loadDirectives, loadKb]);

  const workNow = async () => {
    try {
      const r = await api.bossRun();
      toast.success(r.message);
      setRunning(true);
      await loadState();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start a cycle"); }
  };

  const toggleAutopilot = async (enabled, interval) => {
    try { const s = await api.setBossAutopilot(enabled, interval ?? state.interval_minutes); setState(s); toast[enabled ? "success" : "message"](enabled ? "Autopilot on — LifeOS will keep working on a timer." : "Autopilot paused."); }
    catch { toast.error("Could not update autopilot"); }
  };

  const confirm = async (id) => { const r = await api.confirmDirective(id); toast.success(r.message); await loadDirectives(); await loadState(); };
  const dismiss = async (id) => { await api.dismissDirective(id); toast("Dismissed"); await loadDirectives(); await loadState(); };

  const acceptRec = async (id) => { const r = await api.acceptBossRecommendation(id); toast.success(r.message); setRunning(true); await loadDirectives(); await loadState(); };
  const dismissRec = async (id) => { await api.dismissBossRecommendation(id); await loadDirectives(); };
  const answerQ = async (id, ans) => { await api.answerBossQuestion(id, ans); toast.success("Got it — LifeOS is smarter now."); await loadDirectives(); await loadKb(); };
  const dismissQ = async (id) => { await api.dismissBossQuestion(id); await loadDirectives(); };

  const openRecs = useMemo(() => recs.filter((r) => r.status === "suggested"), [recs]);
  const openQ = useMemo(() => questions.filter((q) => q.status === "open"), [questions]);

  const pending = useMemo(() => directives.filter((d) => d.status === "needs_confirm"), [directives]);
  const handled = useMemo(() => directives.filter((d) => d.status !== "needs_confirm"), [directives]);

  if (!state) return <div className="grid place-items-center h-full text-cyan-300"><Loader2 className="animate-spin" /></div>;

  const brainLabel = state.brain === "own" ? "Your ChatGPT" : "Built-in brain (backup)";

  return (
    <div className="max-w-3xl mx-auto pt-8 px-4 pb-28">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center rounded-xl" style={{ width: 44, height: 44, background: "linear-gradient(135deg,#00f0ff22,#a78bfa22)", border: "1px solid rgba(0,240,255,0.3)" }}>
            <Crown size={22} className="text-cyan-300" />
          </span>
          <div>
            <h2 className="font-display uppercase tracking-widest text-cyan-300 text-lg leading-none">LifeOS · AI Boss</h2>
            <p className="text-xs text-zinc-500 mt-1">Your AI CEO who bosses the workforce into real, finished work.</p>
          </div>
        </div>
        <button data-testid="boss-close-button" onClick={onClose} className="w-9 h-9 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400"><X size={16} /></button>
      </div>

      <BriefingCard briefing={briefing} onRefresh={refreshBriefing} refreshing={refreshingBrief} />

      {/* Control bar */}
      <div className="rounded-xl border border-cyan-400/20 bg-[#09111c] p-4 mb-5">
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="boss-work-now" onClick={workNow} disabled={running}
            className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-[#04070c] text-xs font-bold uppercase tracking-wider rounded-xl px-4 h-10 shadow-[0_0_20px_rgba(0,240,255,0.35)] transition-all disabled:opacity-60">
            {running ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} {running ? "LifeOS is working..." : "Work now"}
          </button>

          <div className="flex items-center gap-2">
            <button data-testid="boss-autopilot-toggle" onClick={() => toggleAutopilot(!state.autopilot)}
              className={`inline-flex items-center gap-1.5 rounded-xl px-3 h-10 text-xs font-semibold border transition-colors ${state.autopilot ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/30" : "bg-white/5 text-zinc-400 border-white/10"}`}>
              <Power size={14} /> Autopilot {state.autopilot ? "ON" : "OFF"}
            </button>
            {state.autopilot && (
              <select data-testid="boss-interval" value={state.interval_minutes} onChange={(e) => toggleAutopilot(true, Number(e.target.value))}
                className="bg-[#05090f] border border-white/10 rounded-xl h-10 px-2 text-xs text-zinc-200">
                {INTERVALS.map((m) => <option key={m} value={m} className="bg-[#0b0e14]">every {m}m</option>)}
              </select>
            )}
          </div>

          <div className="ml-auto text-right">
            <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">Brain</div>
            <div className={`text-xs font-mono ${state.brain === "own" ? "text-emerald-300" : "text-cyan-300"}`}>{brainLabel}</div>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-white/5"><BossChat /></div>
        {state.autopilot && state.next_run_at && (
          <p className="text-[10px] font-mono text-zinc-500 mt-2">Next cycle around {new Date(state.next_run_at).toLocaleTimeString()} · last run {state.last_run_at ? timeAgo(state.last_run_at) + " ago" : "never"}</p>
        )}
      </div>

      {/* Questions from LifeOS */}
      {openQ.length > 0 && (
        <div className="mb-6">
          <h3 className="font-tactical uppercase tracking-wider text-sm text-violet-300 mb-2 flex items-center gap-2"><HelpCircle size={15} /> LifeOS has questions for you ({openQ.length})</h3>
          <div className="space-y-2" data-testid="boss-questions-list">
            {openQ.map((q) => <QuestionCard key={q.id} q={q} onAnswer={answerQ} onDismiss={dismissQ} />)}
          </div>
        </div>
      )}

      {/* Recommended by LifeOS */}
      {openRecs.length > 0 && (
        <div className="mb-6">
          <h3 className="font-tactical uppercase tracking-wider text-sm text-amber-300 mb-2 flex items-center gap-2"><Lightbulb size={15} /> Recommended by LifeOS ({openRecs.length})</h3>
          <div className="space-y-2" data-testid="boss-recommendations-list">
            {openRecs.map((r) => <RecommendationCard key={r.id} r={r} onAccept={acceptRec} onDismiss={dismissRec} />)}
          </div>
        </div>
      )}

      {/* Directives */}
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-tactical uppercase tracking-wider text-sm text-zinc-200">LifeOS's desk {pending.length ? <span className="text-cyan-300">({pending.length} awaiting you)</span> : null}</h3>
      </div>
      {cycles[0] && <p className="text-xs text-zinc-500 mb-3 italic">"{cycles[0].summary}"</p>}

      <div className="space-y-3 mb-8" data-testid="boss-directives-list">
        {pending.length === 0 && handled.length === 0 && (
          <div className="text-center py-12 rounded-xl border border-white/5 bg-white/[0.015]">
            <Crown size={28} className="mx-auto text-cyan-400/40 mb-3" />
            <p className="text-zinc-500 text-sm">Fill your Boss Briefing below, then hit <span className="text-cyan-300">Work now</span>. LifeOS will assign real tasks and deliver finished work here.</p>
          </div>
        )}
        {pending.map((d) => <DirectiveCard key={d.id} d={d} onConfirm={confirm} onDismiss={dismiss} />)}
        {handled.length > 0 && (
          <>
            <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-zinc-600 pt-3 pb-1">Handled</div>
            {handled.slice(0, 12).map((d) => <DirectiveCard key={d.id} d={d} onConfirm={confirm} onDismiss={dismiss} />)}
          </>
        )}
      </div>

      <KnowledgeCard kb={kb} onRefresh={loadKb} onOpenInterview={() => setInterviewOpen(true)} />
      <KeyCard state={state} onChanged={loadState} />
      <ProfileCard profile={state.profile} onSaved={loadState} />

      <p className="text-[11px] text-zinc-500 text-center">Finished work is also saved in <button onClick={onClose} className="text-cyan-300 underline">Deliverables</button>. Connections for real sending live in the <button onClick={onGoConnections} className="text-cyan-300 underline">Connections</button> tab.</p>

      {interviewOpen && <InterviewModal onClose={() => setInterviewOpen(false)} onDone={async () => { setInterviewOpen(false); await loadKb(); }} />}
    </div>
  );
}
